// @ts-ignore
import flickityAsNavFor from "flickity-as-nav-for";
import FlickityClass from "common/utils/ui/flickity-class";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { generatePreview } from "components/jiffy/design-lab/features/manage-product-sizing/ui/generatePreview";
import { track } from "common/utils/mixpanel/tracker";
import { sendDataLayer } from "common/utils/ga";
import { on as onEvent } from "common/utils/ui/event";
import type { printOnDemand } from "common/utils/models";
import "./component.scss";

type ImagePreview = {
  base64: string;
  blob: Blob;
};

type ImagePreviewData = {
  uuid: string;
  compositions: ImagePreviewComposition[];
};

type ImagePreviewComposition = {
  [key: string]: ImagePreviewItem;
};

type ImagePreviewItem = {
  imageUrl: string;
  width: number;
  height: number;
  surface: printOnDemand.getProduct.SurfacesBack;
};

const CLASS_NAMES = {
  ROOT: ".js-design-project-carousel"
};

function $getRoots() {
  return document.querySelectorAll(CLASS_NAMES.ROOT);
}

function getData($root: Element) {
  const data = $root.getAttribute("data-project");
  return data ? (objNamesToCamelCase(JSON.parse(data)) as ImagePreviewData) : null;
}

function createPreviewElement(images: ImagePreview[]) {
  const $element = document.createElement("div");
  $element.className = "design-project-carousel__preview relative size-full";

  images.forEach((image, index) => {
    const $image = document.createElement("img");
    const blobUrl = URL.createObjectURL(image.blob);
    $image.src = blobUrl;
    $image.className = "design-project-carousel__preview-image size-full object-contain";

    $image.onload = () => {
      URL.revokeObjectURL(blobUrl);
    };

    if (index > 0) {
      const $wrapper = document.createElement("div");
      $wrapper.className =
        "bg-background lg:w-15 lg:h-15 absolute bottom-[-1px] left-[50%] h-10 w-10 rounded border border-gray-200";
      $wrapper.appendChild($image);
      $element.appendChild($wrapper);
    } else {
      $element.appendChild($image);
    }
  });

  return $element;
}

function addImgixQuality(url: string, quality: number): string {
  const parsedUrl = new URL(url);
  parsedUrl.searchParams.set("q", String(quality));
  return parsedUrl.toString();
}

async function generateItemPreview(item: ImagePreviewComposition) {
  const sides = Object.values(item);
  const images = await Promise.all(
    sides.map(side =>
      generatePreview({
        imageUrl: addImgixQuality(side.imageUrl, 50),
        artworkSize: {
          width: side.width,
          height: side.height
        },
        surface: {
          ...side.surface,
          picture: addImgixQuality(side.surface.picture, 50)
        }
      })
    )
  );
  return images;
}

async function renderPreviews($root: Element) {
  const data = getData($root);
  if (!data) return;
  const previews = await Promise.all(data.compositions.map(generateItemPreview));

  previews.forEach(preview => {
    const $element = createPreviewElement(preview);
    $root.appendChild($element);
  });

  new FlickityClass(
    `[data-project-uuid="${data.uuid}"]`,
    {
      pageDots: false,
      wrapAround: true,
      fade: true
    },
    {
      library: flickityAsNavFor
    }
  ).initFlickity();
}

type TrackingData = {
  eventName: string;
  pageLocation: string;
  businessLine: string;
  sectionIdentifier: string;
  eventCategory: string;
  eventAction: string;
  projectUuid: string;
  projectName: string;
};

const TRACKING_SELECTOR = "[data-saved-project-add-to-cart]";

function initSavedProjectTracking() {
  onEvent("click", TRACKING_SELECTOR, (event: Event) => {
    const $element = (event.target as HTMLElement).closest(TRACKING_SELECTOR) as HTMLElement;
    if (!$element) return;

    const trackingData: TrackingData = JSON.parse(
      $element.dataset["savedProjectAddToCart"] || "{}"
    );

    track(trackingData.eventName, {
      eventCategory: trackingData.eventCategory,
      page_location: trackingData.pageLocation,
      business_line: trackingData.businessLine,
      section_identifier: trackingData.sectionIdentifier,
      project_uuid: trackingData.projectUuid,
      project_name: trackingData.projectName
    });

    sendDataLayer({
      genericEvent: true,
      eventCategory: trackingData.eventCategory,
      event: trackingData.eventAction,
      page_location: trackingData.pageLocation,
      business_line: trackingData.businessLine,
      section_identifier: trackingData.sectionIdentifier
    });
  });
}

export function init() {
  const $roots = $getRoots();
  if (!$roots.length) return;

  $roots.forEach($root => {
    renderPreviews($root);
  });

  initSavedProjectTracking();
}
